import { useCallback, useEffect, useState } from 'react';
import { ClbLoading } from '@celebration/react/ClbLoading/ClbLoading';
import { useTranslation } from 'react-i18next';
import { ButtonPropsDrawer } from '@celebration/react/ClbDrawer/ClbDrawer.types';
import { INotificationsProps } from './Interfaces/INotificationsProps';
import Drawer from '../../../drawer';
import {
    INotificationsResponse,
    ISystem,
} from './Interfaces/INotificationsResponse';
import DrawerItem from './DrawerItem';
import {
    INotificationType,
    SulaHubNotificationType,
    SmartCargasNotificationType,
} from './Interfaces/INotificationType';
import UsersConstants from '../../../../../pages/users/constants';
import { useNotifications } from '../../../../../app/contexts/notifications/notifications.context';
import RoundsConstants from '../../../../../pages/smartcargas/rounds/constants';
import WonOfferConstants from '../../../../../pages/smartcargas/won-offers/constants';
import { olderThanDays } from '../../../../../utils/date-utils';

function Notifications({
    handleCloseDrawer,
    closeParent,
}: Readonly<INotificationsProps>): JSX.Element {
    const { t } = useTranslation();
    const {
        getAllNotificationsPaginated,
        markAllNotificationsAsRead,
        markNotificationAsRead,
    } = useNotifications();

    const [isLoading, setIsLoading] = useState(false);
    const [notifications, setNotifications] = useState<
        INotificationsResponse[]
    >([]);

    const fetchData = useCallback(async () => {
        setIsLoading(true);

        getAllNotificationsPaginated(setNotifications);

        setIsLoading(false);
    }, [setIsLoading, getAllNotificationsPaginated]);

    const handleOpenDrawer = useCallback(() => {
        handleCloseDrawer();
        closeParent();
    }, [handleCloseDrawer, closeParent]);

    const readAllNotificationsButton: ButtonPropsDrawer = {
        id: 'read-all-notification-button',
        key: 'read-all-notification-button',
        label: t('Marcar todas como lidas'),
        title: t('Marcar todas como lidas'),
        disabled: notifications.length === 0,
        loading: false,
        size: 'md',
        iconPosition: 'none',
        onClick: () => {
            markAllNotificationsAsRead();
            fetchData();
        },
        'data-testid': 'read-all-notification-button',
    };

    const buttonsList: [ButtonPropsDrawer, ButtonPropsDrawer?] = [
        readAllNotificationsButton,
    ];

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const getRouteFromType = (type: INotificationType, system: ISystem) => {
        switch (system) {
            case ISystem.SulaHub:
                switch (type as SulaHubNotificationType) {
                    case SulaHubNotificationType.NewUserRequest:
                    case SulaHubNotificationType.UserUpdateRequest:
                    case SulaHubNotificationType.UserRequestReproved:
                        return UsersConstants.PATH_USER_REQUEST;
                    case SulaHubNotificationType.UserRequestApproved:
                        return UsersConstants.PATH;
                    case SulaHubNotificationType.UserUpdateRequestApproved:
                    case SulaHubNotificationType.UserUpdateRequestReproved:
                    default:
                        return '';
                }
            case ISystem.SmartCargas:
                switch (type as SmartCargasNotificationType) {
                    case SmartCargasNotificationType.LoadConfirmed:
                    case SmartCargasNotificationType.LoadCanceled:
                        return WonOfferConstants.PATH;
                    case SmartCargasNotificationType.NewAcceptanceRound:
                    case SmartCargasNotificationType.NewConfirmationRound:
                        return RoundsConstants.PATH;
                    default:
                        return '';
                }
            default:
                return '';
        }
    };

    const getNotificationDateTime = (notification: INotificationsResponse) => {
        const date = new Date(notification.initialDate);

        return (
            (olderThanDays(date)
                ? date.toLocaleDateString()
                : date.toLocaleTimeString()) ?? ''
        );
    };

    return (
        <Drawer
            title={t('Notificacoes')}
            open
            onClose={handleOpenDrawer}
            buttonsFooter={buttonsList}
            data-testid="notification-drawer"
        >
            <>
                {isLoading ? <ClbLoading size="sm" /> : undefined}

                {!isLoading &&
                    notifications?.map(
                        (notification: INotificationsResponse) => (
                            <DrawerItem
                                key={notification.id}
                                title={notification.title}
                                subtitle={notification.description}
                                date={getNotificationDateTime(notification)}
                                isRead={notification.isRead}
                                actionLink={getRouteFromType(
                                    notification.details.type,
                                    notification.system,
                                )}
                                defaultAction={handleOpenDrawer}
                                onTap={() => {
                                    markNotificationAsRead(notification.id);
                                    fetchData();
                                }}
                                type={notification.details.type}
                                system={notification.system}
                            />
                        ),
                    )}

                {!isLoading && notifications.length === 0 && (
                    <div className="default-message">
                        {t('NenhumaNotificacaoExibida')}
                    </div>
                )}
            </>
        </Drawer>
    );
}

export default Notifications;
